import z from "zod";

import { item } from "../../authoring.js";
import {
  experience,
  need,
  needOrNotApplicable,
  strength,
} from "./responseSets.js";

/**
 * A test instrument shaped like a full CANS, so the machinery around
 * instruments meets something of realistic size and shape: every domain, needs
 * and strengths on scales of their own, and a module rated only where it
 * applies.
 *
 * Modelled on the domains and items of the Praed Foundation's CANS
 * Comprehensive, with the keys in UK spelling. It is test content, not CANS,
 * and this revision declares no meanings: no item text or rating anchors. Its
 * own family, so nothing it asks binds a real CANS published later under a
 * family of its own.
 */

export const name = ["sample_cans", "comprehensive", "1.0", "r1"] as const;

export const groups = {
  life_functioning: "life_functioning",
  behavioural_emotional_needs: "behavioural_emotional_needs",
  risk_behaviours: "risk_behaviours",
  cultural_factors: "cultural_factors",
  strengths: "strengths",
  caregiver_resources_and_needs: "caregiver_resources_and_needs",
  adverse_childhood_experiences: "adverse_childhood_experiences",
} as const;

export const items = {
  family_functioning: "family_functioning",
  living_situation: "living_situation",
  social_functioning: "social_functioning",
  developmental_intellectual: "developmental_intellectual",
  decision_making: "decision_making",
  school_behaviour: "school_behaviour",
  school_achievement: "school_achievement",
  school_attendance: "school_attendance",
  medical_physical: "medical_physical",
  sexual_development: "sexual_development",
  sleep: "sleep",

  psychosis: "psychosis",
  impulsivity_hyperactivity: "impulsivity_hyperactivity",
  depression: "depression",
  anxiety: "anxiety",
  oppositional_behaviour: "oppositional_behaviour",
  conduct: "conduct",
  adjustment_to_trauma: "adjustment_to_trauma",
  anger_control: "anger_control",
  substance_use: "substance_use",

  suicide_risk: "suicide_risk",
  non_suicidal_self_injury: "non_suicidal_self_injury",
  other_self_harm: "other_self_harm",
  danger_to_others: "danger_to_others",
  sexual_aggression: "sexual_aggression",
  runaway: "runaway",
  delinquent_behaviour: "delinquent_behaviour",
  intentional_misbehaviour: "intentional_misbehaviour",

  language: "language",
  traditions_and_rituals: "traditions_and_rituals",
  cultural_stress: "cultural_stress",

  family_strengths: "family_strengths",
  interpersonal: "interpersonal",
  educational_setting: "educational_setting",
  talents_and_interests: "talents_and_interests",
  spiritual_religious: "spiritual_religious",
  cultural_identity: "cultural_identity",
  community_life: "community_life",
  natural_supports: "natural_supports",
  resilience: "resilience",

  caregiver_supervision: "caregiver_supervision",
  caregiver_involvement_with_care: "caregiver_involvement_with_care",
  caregiver_knowledge: "caregiver_knowledge",
  caregiver_organisation: "caregiver_organisation",
  caregiver_social_resources: "caregiver_social_resources",
  caregiver_residential_stability: "caregiver_residential_stability",
  caregiver_medical_physical: "caregiver_medical_physical",
  caregiver_mental_health: "caregiver_mental_health",
  caregiver_substance_use: "caregiver_substance_use",
  caregiver_developmental: "caregiver_developmental",
  caregiver_safety: "caregiver_safety",

  sexual_abuse: "sexual_abuse",
  physical_abuse: "physical_abuse",
  emotional_abuse: "emotional_abuse",
  neglect: "neglect",
  medical_trauma: "medical_trauma",
  witness_to_family_violence: "witness_to_family_violence",
  witness_to_community_violence: "witness_to_community_violence",
  natural_or_man_made_disaster: "natural_or_man_made_disaster",
  war_or_terrorism_affected: "war_or_terrorism_affected",
  victim_or_witness_to_criminal_activity:
    "victim_or_witness_to_criminal_activity",
  disrupted_caregiving: "disrupted_caregiving",
  parental_criminal_behaviour: "parental_criminal_behaviour",
} as const;

/** Every key an earlier version asked and this one does not. The first version retires nothing. */
export const retiredItems = [] as const;

const lifeFunctioning = z
  .object({
    ...item(items.family_functioning, need).withComment().build(),
    ...item(items.living_situation, need).withComment().build(),
    ...item(items.social_functioning, need).withComment().build(),
    ...item(items.developmental_intellectual, need).withComment().build(),
    ...item(items.decision_making, need).withComment().build(),
    // Not applicable where the young person is not in education, which is a
    // different fact from having no need there.
    ...item(items.school_behaviour, needOrNotApplicable).withComment().build(),
    ...item(items.school_achievement, needOrNotApplicable)
      .withComment()
      .build(),
    ...item(items.school_attendance, needOrNotApplicable).withComment().build(),
    ...item(items.medical_physical, need).withComment().build(),
    ...item(items.sexual_development, need).withComment().build(),
    ...item(items.sleep, need).withComment().build(),
  })
  .strict();

const behaviouralEmotionalNeeds = z
  .object({
    ...item(items.psychosis, need).withComment().build(),
    ...item(items.impulsivity_hyperactivity, need).withComment().build(),
    ...item(items.depression, need).withComment().build(),
    ...item(items.anxiety, need).withComment().build(),
    ...item(items.oppositional_behaviour, need).withComment().build(),
    ...item(items.conduct, need).withComment().build(),
    ...item(items.adjustment_to_trauma, need).withComment().build(),
    ...item(items.anger_control, need).withComment().build(),
    ...item(items.substance_use, need).withComment().build(),
  })
  .strict();

const riskBehaviours = z
  .object({
    ...item(items.suicide_risk, need).withComment().build(),
    ...item(items.non_suicidal_self_injury, need).withComment().build(),
    ...item(items.other_self_harm, need).withComment().build(),
    ...item(items.danger_to_others, need).withComment().build(),
    ...item(items.sexual_aggression, need).withComment().build(),
    ...item(items.runaway, need).withComment().build(),
    ...item(items.delinquent_behaviour, need).withComment().build(),
    ...item(items.intentional_misbehaviour, need).withComment().build(),
  })
  .strict();

const culturalFactors = z
  .object({
    ...item(items.language, need).withComment().build(),
    ...item(items.traditions_and_rituals, need).withComment().build(),
    ...item(items.cultural_stress, need).withComment().build(),
  })
  .strict();

const strengths = z
  .object({
    ...item(items.family_strengths, strength).withComment().build(),
    ...item(items.interpersonal, strength).withComment().build(),
    ...item(items.educational_setting, strength).withComment().build(),
    ...item(items.talents_and_interests, strength).withComment().build(),
    ...item(items.spiritual_religious, strength).withComment().build(),
    ...item(items.cultural_identity, strength).withComment().build(),
    ...item(items.community_life, strength).withComment().build(),
    ...item(items.natural_supports, strength).withComment().build(),
    ...item(items.resilience, strength).withComment().build(),
  })
  .strict();

// Every key names the caregiver: several would otherwise be the same word as a
// young person's item, and a key means one thing across a family.
const caregiverResourcesAndNeeds = z
  .object({
    ...item(items.caregiver_supervision, need).withComment().build(),
    ...item(items.caregiver_involvement_with_care, need).withComment().build(),
    ...item(items.caregiver_knowledge, need).withComment().build(),
    ...item(items.caregiver_organisation, need).withComment().build(),
    ...item(items.caregiver_social_resources, need).withComment().build(),
    ...item(items.caregiver_residential_stability, need).withComment().build(),
    ...item(items.caregiver_medical_physical, need).withComment().build(),
    ...item(items.caregiver_mental_health, need).withComment().build(),
    ...item(items.caregiver_substance_use, need).withComment().build(),
    ...item(items.caregiver_developmental, need).withComment().build(),
    ...item(items.caregiver_safety, need).withComment().build(),
  })
  .strict();

const adverseChildhoodExperiences = z
  .object({
    ...item(items.sexual_abuse, experience).withComment().build(),
    ...item(items.physical_abuse, experience).withComment().build(),
    ...item(items.emotional_abuse, experience).withComment().build(),
    ...item(items.neglect, experience).withComment().build(),
    ...item(items.medical_trauma, experience).withComment().build(),
    ...item(items.witness_to_family_violence, experience).withComment().build(),
    ...item(items.witness_to_community_violence, experience)
      .withComment()
      .build(),
    ...item(items.natural_or_man_made_disaster, experience)
      .withComment()
      .build(),
    ...item(items.war_or_terrorism_affected, experience).withComment().build(),
    ...item(items.victim_or_witness_to_criminal_activity, experience)
      .withComment()
      .build(),
    ...item(items.disrupted_caregiving, experience).withComment().build(),
    ...item(items.parental_criminal_behaviour, experience)
      .withComment()
      .build(),
  })
  .strict();

export const schema = z
  .object({
    [groups.life_functioning]: lifeFunctioning,
    [groups.behavioural_emotional_needs]: behaviouralEmotionalNeeds,
    [groups.risk_behaviours]: riskBehaviours,
    [groups.cultural_factors]: culturalFactors,
    [groups.strengths]: strengths,
    // A module, rated only where there is a caregiver to rate.
    [groups.caregiver_resources_and_needs]:
      caregiverResourcesAndNeeds.optional(),
    [groups.adverse_childhood_experiences]: adverseChildhoodExperiences,
  })
  .strict();
